import { CategoryType } from '@core/shared/domain';

/**
 * The possible article range filter types.
 */
export enum ArticleRangeFilterType {
  /**
   * A group of sub filters, combined with `AND` or `OR`.
   */
  Group = 'Group',
  /**
   * Filter by category type and code.
   */
  Category = 'Category',
  /**
   * Filter by manufacturer (platform supplier id).
   */
  Manufacturer = 'Manufacturer',
  /**
   * Filter by supplier (platform supplier id).
   */
  Supplier = 'Supplier',
}

/**
 * Operators for `Group` filters.
 */
export enum GroupArticleRangeFilterOperator {
  /**
   * All sub filters must match.
   */
  And = 'And',
  /**
   * At least one sub filter must match.
   */
  Or = 'Or',
}

/**
 * Operators for field filters.
 */
export enum FieldArticleRangeFilterOperator {
  /**
   * Article must have at least one value of the list of filter values.
   */
  Equals = 'Equals',
  /**
   * Article must not have any value of the list of filter values.
   */
  NotEquals = 'NotEquals',
  /**
   * Article must not have any value set. The filter values are not used.
   */
  Empty = 'Empty',
  /**
   * Article must have at least one value set. The filter values are not used.
   */
  NotEmpty = 'NotEmpty',
}

/**
 * Base interface for filter definitions.
 */
interface ArticleRangeFilterBase {
  /**
   * The filter type.
   */
  type: ArticleRangeFilterType;
}

/**
 * Describes a `Group` filter. This is a group of sub filters that are combined with a conditional operator.
 */
export interface GroupArticleRangeFilter extends ArticleRangeFilterBase {
  type: ArticleRangeFilterType.Group;

  /**
   * The conditional operator.
   */
  operator: GroupArticleRangeFilterOperator;
  /**
   * The list of child filters.
   */
  children: ArticleRangeFilter[];
}

/**
 * Describes a `Category` filter.
 */
export interface CategoryArticleRangeFilter extends ArticleRangeFilterBase {
  type: ArticleRangeFilterType.Category;
  /**
   * The operator to use.
   */
  operator: FieldArticleRangeFilterOperator;
  /**
   * The category type to compare.
   */
  categoryType: CategoryType;
  /**
   * The category codes as filter values.
   */
  codes?: string[] | null;
}

/**
 * Describes a `Manufacturer` filter.
 */
export interface ManufacturerArticleRangeFilter extends ArticleRangeFilterBase {
  type: ArticleRangeFilterType.Manufacturer;
  /**
   * The operator to use.
   */
  operator: FieldArticleRangeFilterOperator;
  /**
   * The platform supplier ids as filter values.
   */
  manufacturerIds?: string[] | null;
}

/**
 * Describes a `Supplier` filter.
 */
export interface SupplierArticleRangeFilter extends ArticleRangeFilterBase {
  type: ArticleRangeFilterType.Supplier;
  /**
   * The operator to use.
   */
  operator: FieldArticleRangeFilterOperator;
  /**
   * The platform supplier ids as filter values.
   */
  supplierIds?: string[] | null;
}

/**
 * Describes any filter.
 */
export type ArticleRangeFilter =
  | GroupArticleRangeFilter
  | CategoryArticleRangeFilter
  | ManufacturerArticleRangeFilter
  | SupplierArticleRangeFilter;
